import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation } from '@apollo/client';
import { Loader2Icon } from 'lucide-react';
import { PhoneInput } from 'react-international-phone';
import 'react-international-phone/style.css';
import { toast } from 'sonner';

import {
  Button,
  DatePicker,
  Dialog,
  DialogFooter,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  useErrorHandler,
} from '@sandtech-hub-monorepo/ui-kit';
import {
  CountryPicker,
  StatePicker,
  toCalendarDate,
} from '@sandtech-hub-monorepo/ui-kit-site';
import { Gender, UpdateUserDocument } from '@sandtech-hub-monorepo/ehub-client';

const genderLabels: Record<Gender, string> = {
  [Gender.Female]: 'Female',
  [Gender.Male]: 'Male',
  [Gender.NonBinary]: 'Non-binary',
  [Gender.Other]: 'Others',
  [Gender.PreferNotToSay]: 'Prefer not to say',
};

const currentYear = new Date().getFullYear();

const formSchema = z.object({
  birthDate: z.date().optional(),
  gender: z.nativeEnum(Gender).optional(),
  phoneNumber: z.string().optional(),
  countryOfOrigin: z.string().optional(),
  countryOfResidence: z.string().optional(),
  countyOfResidence: z.string().optional(),
});

type FormValues = z.infer<typeof formSchema>;

interface Props {
  open: boolean;
  handleOpenChange: (open: boolean) => void;
  defaultValues: FormValues;
}

export const PersonalInfoEditDialog = ({
  open,
  handleOpenChange,
  defaultValues,
}: Props) => {
  const { handleGeneralAppError } = useErrorHandler();
  const [updateUser, { loading }] = useMutation(UpdateUserDocument, {
    onError: (error) => handleGeneralAppError(error.message),
    onCompleted: () => {
      toast.success('Personal information updated.');
      handleOpenChange(false);
    },
  });

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues,
  });

  // Keyed on `open` rather than `defaultValues` — see ProfileEditDialog for
  // why. The call site here rebuilds a `Date` via `parseCalendarDate` on every
  // render, so the object identity was guaranteed to change.
  useEffect(() => {
    if (open) {
      form.reset(defaultValues);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleSubmit = (values: FormValues) => {
    updateUser({
      variables: {
        input: {
          // A calendar day, not an instant - see the `Date` scalar note in
          // the onboarding form.
          birthDate: values.birthDate
            ? toCalendarDate(values.birthDate)
            : undefined,
          gender: values.gender,
          phoneNumber: values.phoneNumber,
          countryOfOrigin: values.countryOfOrigin,
          countryOfResidence: values.countryOfResidence,
          countyOfResidence: values.countyOfResidence,
        },
      },
    });
  };

  return (
    <Dialog
      open={open}
      onOpenChange={handleOpenChange}
      title="Edit personal information"
      description="Update your personal details"
      className="bg-card"
    >
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(handleSubmit)}
          className="flex flex-col gap-4"
        >
          <FormField
            control={form.control}
            name="birthDate"
            render={({ field }) => (
              <FormItem className="flex flex-col">
                <FormLabel className="font-normal">Date of Birth</FormLabel>
                <FormControl>
                  <DatePicker
                    selected={field.value}
                    onSelect={field.onChange}
                    placeholder="DD / MM / YYYY"
                    fromYear={1940}
                    toYear={currentYear}
                    disabledDates={(date) => date > new Date()}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="gender"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="font-normal">Gender</FormLabel>
                <Select onValueChange={field.onChange} value={field.value}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select your gender" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {Object.values(Gender).map((value) => (
                      <SelectItem key={value} value={value}>
                        {genderLabels[value]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="phoneNumber"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="font-normal">Phone</FormLabel>
                <FormControl>
                  <PhoneInput
                    defaultCountry="us"
                    value={field.value || ''}
                    onChange={field.onChange}
                    inputClassName="!w-full !h-10 !rounded-md !border-input !bg-background !text-sm !text-foreground"
                    countrySelectorStyleProps={{
                      buttonClassName: '!h-10 !rounded-l-md !border-input',
                    }}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="countryOfOrigin"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="font-normal">Nationality</FormLabel>
                <FormControl>
                  <CountryPicker
                    selected={field.value || undefined}
                    handleOnSelect={({ value }) => field.onChange(value)}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="countryOfResidence"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="font-normal">
                    Country of Residence
                  </FormLabel>
                  <FormControl>
                    <CountryPicker
                      selected={field.value || undefined}
                      handleOnSelect={({ value }) => {
                        // A state only means something within its own
                        // country, so switching country drops the stale one
                        // rather than leaving a mismatched pair to submit.
                        if (value !== field.value) {
                          form.setValue('countyOfResidence', '');
                        }
                        field.onChange(value);
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="countyOfResidence"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="font-normal">
                    State of Residence
                  </FormLabel>
                  <FormControl>
                    <StatePicker
                      countryCode={
                        form.watch('countryOfResidence') || undefined
                      }
                      selected={field.value || undefined}
                      handleOnSelect={({ value }) => field.onChange(value)}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <DialogFooter className="mt-4 flex flex-col sm:flex-row gap-1">
            <Button
              type="button"
              variant="outline"
              onClick={() => handleOpenChange(false)}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? <Loader2Icon className="animate-spin" /> : 'Save'}
            </Button>
          </DialogFooter>
        </form>
      </Form>
    </Dialog>
  );
};
